import { useMemo } from 'react';
import { OneOfListElements } from '@shared/models/project.model';
import { GroupSectionData } from '@ui/ElementList/list.model';
import { useGroupSectionChartData } from './use-group-section-charts.hook';
import { useProposalResultsRecord } from '@features/proposals/use-proposals.hooks';
import { useItemsSelectedProposalColor } from '@features/proposals/hooks/use-proposals.hooks';
import { getCo2eChartDataFromResults } from '@ui/BarChart/helpers/bar-chart.helpers';
import { useGetResultsPerGFA } from '@features/results/use-results.hooks';
import {
  getConversionFactorValue,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { filterListElementsContributingToVersionTotals } from '@shared/helpers/element-list-group-contributors.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { useSelectedVersion } from '@client/store/ui';
import { GroupSectionMeta } from '../element-grouping.model';

export type SbefGroupSectionBarsData = {
  sekValues: ReturnType<typeof useGroupSectionChartData>['sekValues'];
  co2eMax: number;
  sekMax: number;
  proposalBars: Array<{
    id: string;
    color: string;
    co2Values: ReturnType<typeof getCo2eChartDataFromResults>;
  }>;
  co2ePerGFA: number;
  sekPerGFA: number;
};

export const useSbefGroupSectionBarsData = (
  section: GroupSectionData<OneOfListElements, GroupSectionMeta>,
): SbefGroupSectionBarsData => {
  const { sekValues, co2eMax, sekMax } = useGroupSectionChartData(section);
  const getResultsPerGFA = useGetResultsPerGFA();
  const version = useSelectedVersion();

  // Align with {@link useGroupSectionProps} `resultsSum`: contributing roots only.
  const itemIds = useMemo(
    () =>
      filterListElementsContributingToVersionTotals(section.items, version)
        .filter(isElement)
        .map((el) => el.id),
    [section.items, version],
  );

  // Color the bar with the selected proposal's color only when at least one
  // element in this group is affected by that proposal.
  const proposalColor = useItemsSelectedProposalColor(itemIds);
  // proposal: results record for the currently selected proposal (or baseline)
  const selectedProposalRecord = useProposalResultsRecord();

  const aggregatedPerGFA = useMemo(
    () => getResultsPerGFA(section.meta.resultsSum),
    [section.meta.resultsSum, getResultsPerGFA],
  );
  const co2ePerGFA = getConversionFactorValue(aggregatedPerGFA, 'co2e');
  const sekPerGFA = getConversionFactorValue(aggregatedPerGFA, 'sek');

  const proposalBars = useMemo(() => {
    const summed = sumConversionFactors(
      ...itemIds.map((id) => selectedProposalRecord[id] ?? {}),
    );
    const perGFA = getResultsPerGFA(summed);
    return [
      {
        id: 'proposal',
        color: proposalColor,
        co2Values: getCo2eChartDataFromResults(perGFA),
      },
    ];
  }, [itemIds, selectedProposalRecord, proposalColor, getResultsPerGFA]);

  return {
    sekValues,
    co2eMax,
    sekMax,
    proposalBars,
    co2ePerGFA,
    sekPerGFA,
  };
};
